// The Water layer in the 3D view: stream channels from the LIDAR (painted on the ground), the points where they sink,
// streams through the caves (station to station, with arrows downstream) and the connections between where water
// is seen (dye traces and the like: dashed lines with an arrow).  Mixed into Viewer (see the end of viewer.js).
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// what each kind of channel and connection looks like (also used for the legend)
export const CHANNEL_STYLE = {
  perennial: { color: '#2f6ff0', edge: '#0b1f4d', w: 8, label: 'Perennial stream (on the hydrology map)' },
  sinking: { color: '#2f6ff0', edge: '#0b1f4d', w: 6, dash: [40, 18], label: 'Sinking / intermittent (on the map)' },
  losing: { color: '#7fe3e3', w: 4, dash: [14, 10], label: 'Channel on limestone: likely losing' },
  lidar: { color: '#79b4f2', w: 3, label: 'Channel in the LIDAR (dry most of the year)' },
};
export const LINK_STYLE = {
  dye: { color: 0xe04fd0, css: '#e04fd0', w: 3.2, label: 'Dye trace' },
  reported: { color: 0xf0a6e6, css: '#f0a6e6', w: 1.6, op: 0.6, label: 'Reported (map or report)' },
  inferred: { color: 0x9fb4d6, css: '#9fb4d6', w: 1.6, op: 0.7, label: 'Inferred' },
  negative: { color: 0xe0452b, css: '#e0452b', w: 2, label: 'Traced, not found (negative)' },
};
export const ROUTE_COLOR = '#38a0ff';
const EXIT_COLOR = { sump: '#1e3a8a', sink: '#1e3a8a', joins: '#38a0ff', resurges: '#22d3ee', unknown: '#9ca3af' };

// round off the 5 m grid's zig-zags (Chaikin's corner cutting, ends kept)
function smooth(p, n = 2) {
  for (let k = 0; k < n && p.length > 2; k++) {
    const q = [p[0]];
    for (let i = 0; i < p.length - 1; i++) {
      const [ax, ay] = p[i], [bx, by] = p[i + 1];
      q.push([0.75 * ax + 0.25 * bx, 0.75 * ay + 0.25 * by], [0.25 * ax + 0.75 * bx, 0.25 * ay + 0.75 * by]);
    }
    q.push(p[p.length - 1]); p = q;
  }
  return p;
}

export function channelKind(r) {
  if (r.map === 'perennial') return 'perennial';
  if (r.map === 'sinking') return 'sinking';
  if ((r.lime ?? 0) >= 0.5) return 'losing';
  return 'lidar';
}

function dotTexture(shape) {
  const cv = document.createElement('canvas'); cv.width = cv.height = 48; const x = cv.getContext('2d');
  x.beginPath();
  if (shape === 'tri') { x.moveTo(6, 10); x.lineTo(42, 10); x.lineTo(24, 41); x.closePath(); }
  else x.arc(24, 24, 15, 0, Math.PI * 2);
  x.fillStyle = '#ffffff'; x.fill(); x.lineWidth = 5; x.strokeStyle = '#141413'; x.stroke();
  return new THREE.CanvasTexture(cv);
}

export const WaterMixin = {
  waterMat(key, o) {
    this.wMats ||= {};
    if (!this.wMats[key]) {
      const m = new LineMaterial({ transparent: true, depthTest: false, ...o });
      m.resolution.set(innerWidth, innerHeight); this.lineMats.push(m); this.wMats[key] = m;
    }
    return this.wMats[key];
  },

  // ---------------------------------------------------------------- channels on the ground
  setChannels(doc) {
    this.channelsDoc = doc;
    if (this.sinkPtObj) { this.world.remove(this.sinkPtObj); this.sinkPtObj.geometry.dispose(); this.sinkPtObj = null; }
    const T = this.terrain?.shared;
    if (!doc || !this.origin || !T || !doc.reaches?.length) { this.applyWater(); return; }
    const [xc, yc, zc] = this.origin;
    let x0 = 1e12, x1 = -1e12, z0 = 1e12, z1 = -1e12;
    for (const r of doc.reaches) for (const [E, N] of r.p) { const x = E - xc, z = -(N - yc); x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z); }
    const pad = 40; x0 -= pad; x1 += pad; z0 -= pad; z1 += pad;
    const k = 4096 / Math.max(x1 - x0, z1 - z0);
    const cv = document.createElement('canvas'); cv.width = Math.ceil((x1 - x0) * k); cv.height = Math.ceil((z1 - z0) * k);
    const g = cv.getContext('2d'); g.lineJoin = 'round'; g.lineCap = 'round';
    const P = (E, N) => [(E - xc - x0) * k, (-(N - yc) - z0) * k];
    const groups = { lidar: [], losing: [], sinking: [], perennial: [] };
    for (const r of doc.reaches) groups[channelKind(r)].push(r);
    const stroke = (list, color, wm, dash) => {
      g.strokeStyle = color; g.lineWidth = Math.max(1.4, wm * k); g.setLineDash(dash ? dash.map(d => d * k) : []);
      for (const r of list) {
        g.beginPath(); (r._s ||= smooth(r.p)).forEach(([E, N], i) => { const [x, y] = P(E, N); i ? g.lineTo(x, y) : g.moveTo(x, y); }); g.stroke();
      }
    };
    for (const kind of ['lidar', 'losing', 'sinking', 'perennial']) {
      const st = CHANNEL_STYLE[kind], L = groups[kind];
      const wOf = (r) => st.w * (kind === 'lidar' || kind === 'losing' ? (r.o >= 4 ? 1.6 : r.o >= 3 ? 1.3 : 1) : 1);
      for (const w of new Set(L.map(wOf))) {
        const sub = L.filter(r => wOf(r) === w);
        g.globalAlpha = kind === 'lidar' ? 0.8 : 1;
        if (st.edge) stroke(sub, st.edge, w + 4, st.dash);
        stroke(sub, st.color, w, st.dash);
      }
    }
    g.globalAlpha = 1; g.setLineDash([]);
    const tex = new THREE.CanvasTexture(cv);
    tex.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    const old = T.streamTex.value; T.streamTex.value = tex; if (old !== this.terrain.blankDep) old.dispose();
    T.streamBounds.value.set(x0, x1, z0, z1);
    // where the channels sink: blue triangles
    const S = doc.sinks || [];
    if (S.length) {
      const pos = new Float32Array(3 * S.length), col = new Float32Array(3 * S.length), c = new THREE.Color('#2f6ff0');
      S.forEach((s, i) => { pos.set([s.e - xc, (s.z ?? this.groundAt(s.e, s.n)) - zc + 1, -(s.n - yc)], 3 * i); col.set([c.r, c.g, c.b], 3 * i); });
      const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.BufferAttribute(pos, 3)); geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
      this.triTex ||= dotTexture('tri');
      const m = new THREE.PointsMaterial({ size: 17, sizeAttenuation: false, vertexColors: true, map: this.triTex, transparent: true, alphaTest: 0.3, depthTest: false });
      this.sinkPtObj = new THREE.Points(geo, m); this.sinkPtObj.renderOrder = 38; this.world.add(this.sinkPtObj); this.clipObject(this.sinkPtObj, true);
    }
    this.applyWater();
  },

  // ---------------------------------------------------------------- streams in caves and connections
  // doc: {routes: [{id, name, stations, pts, exit_kind, flow}], links: [{id, evidence, a: {pos, ...}, b: {pos, ...}}]}
  setWater(doc) {
    this.waterDoc = doc;
    this.drawRoutes(); this.drawLinks(); this.highlightWater(this.waterSel); this.applyWater();
  },

  // a route's points in cave-local coordinates, split where a station is not in the model
  routeRuns(r) {
    const runs = []; let cur = [];
    const S = this.stPos;
    for (const st of r.stations || []) {
      const i = this.nameIdx?.get(st);
      if (i == null) { if (cur.length > 1) runs.push(cur); cur = []; continue; }
      cur.push(new THREE.Vector3(S[3 * i], S[3 * i + 1], S[3 * i + 2]));
    }
    if (cur.length > 1) runs.push(cur);
    return runs;
  },

  drawRoutes() {
    if (this.routeObj) { this.cave.remove(this.routeObj); this.routeObj.traverse(o => o.geometry?.dispose()); this.routeObj = null; }
    this.routeHits = [];
    const R = this.waterDoc?.routes || [];
    if (!this.M || !R.length) { this.labels.setWater?.([]); return; }
    const grp = new THREE.Group(), solid = [], dashed = [], chev = [], dots = [], dcol = [], labels = [], c = new THREE.Color();
    const up = new THREE.Vector3(0, 1, 0), d = new THREE.Vector3(), q = new THREE.Vector3(), side = new THREE.Vector3();
    for (const r of R) {
      const runs = this.routeRuns(r); if (!runs.length) continue;
      const arr = r.flow === 'intermittent' ? dashed : solid;
      for (const run of runs) {
        for (let k = 1; k < run.length; k++) arr.push(run[k - 1].x, run[k - 1].y, run[k - 1].z, run[k].x, run[k].y, run[k].z);
        let acc = 0, next = 20;                            // a chevron every 45 m, pointing downstream
        for (let k = 1; k < run.length; k++) {
          const a = run[k - 1], b = run[k], L = a.distanceTo(b); if (L < 0.01) continue;
          d.subVectors(b, a).normalize(); side.crossVectors(d, up); if (side.lengthSq() < 1e-6) side.set(1, 0, 0); side.normalize();
          for (; next <= acc + L; next += 45) {
            q.copy(a).addScaledVector(d, next - acc);
            const s = 3.2;
            for (const sg of [1, -1]) chev.push(q.x - d.x * s + side.x * s * 0.8 * sg, q.y - d.y * s + side.y * s * 0.8 * sg, q.z - d.z * s + side.z * s * 0.8 * sg, q.x, q.y, q.z);
          }
          acc += L;
        }
      }
      const first = runs[0][0], last = runs[runs.length - 1].at(-1);
      dots.push(first.x, first.y, first.z); c.set(ROUTE_COLOR); dcol.push(c.r, c.g, c.b);
      dots.push(last.x, last.y, last.z); c.set(EXIT_COLOR[r.exit_kind] || EXIT_COLOR.unknown); dcol.push(c.r, c.g, c.b);
      this.routeHits.push({ r, runs });
      const all = runs.flat(), mid = all[Math.floor(all.length / 2)];
      labels.push({ text: r.name, x: mid.x, y: mid.y, z: mid.z, id: r.id });
    }
    const line = (arr, mat) => { if (!arr.length) return; const geo = new LineSegmentsGeometry(); geo.setPositions(arr); const l = new LineSegments2(geo, mat); l.computeLineDistances(); l.renderOrder = 41; grp.add(l); };
    line(solid, this.waterMat('route', { color: ROUTE_COLOR, linewidth: 4.5, opacity: 0.95 }));
    line(dashed, this.waterMat('routeDash', { color: ROUTE_COLOR, linewidth: 4.5, opacity: 0.95, dashed: true, dashSize: 6, gapSize: 4 }));
    line(chev, this.waterMat('routeChev', { color: 0xe8f4ff, linewidth: 2.2, opacity: 0.95 }));
    if (dots.length) {
      const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(dots, 3)); geo.setAttribute('color', new THREE.Float32BufferAttribute(dcol, 3));
      this.dotTex ||= dotTexture('dot');
      const p = new THREE.Points(geo, new THREE.PointsMaterial({ size: 12, sizeAttenuation: false, vertexColors: true, map: this.dotTex, transparent: true, alphaTest: 0.3, depthTest: false }));
      p.renderOrder = 42; grp.add(p);
    }
    this.routeObj = grp; this.cave.add(grp); this.clipObject(grp);
    this.labels.setWater?.(labels);
  },

  // local position of one end of a connection (an underground end follows the datum shift), or null
  linkEnd(e) {
    if (!e?.pos || e.pos[0] == null || !this.origin) return null;
    const [E, N, Z] = e.pos, v = this.toLocal(E, N, Z ?? null);
    if (e.station || e.place || e.route || (e.cave_id && !e.spring)) v.y += this.opts.shift;
    return v;
  },

  drawLinks() {
    if (this.linkObj) { this.world.remove(this.linkObj); this.linkObj.traverse(o => o.geometry?.dispose()); this.linkObj = null; }
    this.linkHits = [];
    const Ls = this.waterDoc?.links || [];
    if (!this.origin || !Ls.length) return;
    const grp = new THREE.Group(), lines = {}, marks = {};
    const up = new THREE.Vector3(0, 1, 0), d = new THREE.Vector3(), side = new THREE.Vector3(), q = new THREE.Vector3();
    for (const l of Ls) {
      const a = this.linkEnd(l.a), b = this.linkEnd(l.b); if (!a || !b) continue;
      const ev = LINK_STYLE[l.evidence] ? l.evidence : 'inferred', len = a.distanceTo(b); if (len < 0.5) continue;
      (lines[ev] ||= []).push(a.x, a.y, a.z, b.x, b.y, b.z);
      d.subVectors(b, a).normalize(); side.crossVectors(d, up); if (side.lengthSq() < 1e-6) side.set(1, 0, 0); side.normalize();
      const s = Math.max(8, Math.min(40, len * 0.04)), M = marks[ev] ||= [];
      q.lerpVectors(a, b, 0.55);
      if (ev === 'negative') {                 // a cross half way: water did not show up here
        const u = new THREE.Vector3().crossVectors(side, d).normalize();
        for (const [p1, p2] of [[side, u], [side, u.clone().negate()]]) M.push(q.x - p1.x * s * 0.6 - p2.x * s * 0.6, q.y - p1.y * s * 0.6 - p2.y * s * 0.6, q.z - p1.z * s * 0.6 - p2.z * s * 0.6,
          q.x + p1.x * s * 0.6 + p2.x * s * 0.6, q.y + p1.y * s * 0.6 + p2.y * s * 0.6, q.z + p1.z * s * 0.6 + p2.z * s * 0.6);
      } else for (const sg of [1, -1]) M.push(q.x - d.x * s + side.x * s * 0.6 * sg, q.y - d.y * s + side.y * s * 0.6 * sg, q.z - d.z * s + side.z * s * 0.6 * sg, q.x, q.y, q.z);
      this.linkHits.push({ l, a, b, ev });
    }
    for (const [ev, arr] of Object.entries(lines)) {
      const st = LINK_STYLE[ev];
      const geo = new LineSegmentsGeometry(); geo.setPositions(arr);
      const line = new LineSegments2(geo, this.waterMat('link:' + ev, { color: st.color, linewidth: st.w, opacity: st.op ?? 0.95, dashed: true, dashSize: ev === 'negative' ? 12 : 30, gapSize: ev === 'negative' ? 10 : 16 }));
      line.computeLineDistances(); line.renderOrder = 40; line.userData.ev = ev; grp.add(line);
      const g2 = new LineSegmentsGeometry(); g2.setPositions(marks[ev]);
      const m = new LineSegments2(g2, this.waterMat('linkMark:' + ev, { color: st.color, linewidth: st.w + 0.6, opacity: Math.min(1, (st.op ?? 0.95) + 0.1) })); m.renderOrder = 40; m.userData.ev = ev; grp.add(m);
    }
    this.linkObj = grp; this.world.add(grp); this.clipObject(grp, true);
  },

  // the selected stream or connection drawn over in yellow; sel = {type: 'stream' | 'trace', id} or null
  highlightWater(sel) {
    this.waterSel = sel;
    if (this.waterHl) { this.waterHl.parent?.remove(this.waterHl); this.waterHl.geometry.dispose(); this.waterHl = null; }
    if (!sel) { this.dirty = true; return; }
    const arr = [];
    let parent = null;
    if (sel.type === 'stream') {
      const h = (this.routeHits || []).find(x => x.r.id === sel.id); if (!h) return;
      for (const run of h.runs) for (let k = 1; k < run.length; k++) arr.push(run[k - 1].x, run[k - 1].y, run[k - 1].z, run[k].x, run[k].y, run[k].z);
      parent = this.cave;
    } else if (sel.type === 'trace') {
      const h = (this.linkHits || []).find(x => x.l.id === sel.id); if (!h) return;
      arr.push(h.a.x, h.a.y, h.a.z, h.b.x, h.b.y, h.b.z); parent = this.world;
    }
    if (!arr.length) return;
    const geo = new LineSegmentsGeometry(); geo.setPositions(arr);
    this.waterHl = new LineSegments2(geo, this.waterMat('hl', { color: 0xffd23f, linewidth: 8, opacity: 0.55 }));
    this.waterHl.renderOrder = 39; parent.add(this.waterHl); this.dirty = true;
  },

  // a stream being edited: its stations drawn in yellow (null clears it)
  previewRoute(stations) {
    if (this.waterPrev) { this.cave.remove(this.waterPrev); this.waterPrev.geometry.dispose(); this.waterPrev = null; }
    if (stations?.length > 1 && this.M) {
      const runs = this.routeRuns({ stations }), arr = [];
      for (const run of runs) for (let k = 1; k < run.length; k++) arr.push(run[k - 1].x, run[k - 1].y, run[k - 1].z, run[k].x, run[k].y, run[k].z);
      if (arr.length) {
        const geo = new LineSegmentsGeometry(); geo.setPositions(arr);
        this.waterPrev = new LineSegments2(geo, this.waterMat('prev', { color: 0xffd23f, linewidth: 6, opacity: 0.9 }));
        this.waterPrev.renderOrder = 43; this.cave.add(this.waterPrev);
      }
    }
    this.dirty = true;
  },

  // which parts are on: opts.waterChannels, waterRoutes, waterLinks, waterNeg
  applyWater() {
    const o = this.opts, T = this.terrain?.shared;
    if (T) T.streamOn.value = o.waterChannels && this.channelsDoc?.reaches?.length ? 1 : 0;
    if (this.sinkPtObj) this.sinkPtObj.visible = !!o.waterChannels;
    if (this.routeObj) this.routeObj.visible = !!o.waterRoutes;
    if (this.linkObj) for (const c of this.linkObj.children) c.visible = !!o.waterLinks && (c.userData.ev !== 'negative' || !!o.waterNeg);
    if (this.waterHl) this.waterHl.visible = this.waterSel?.type === 'stream' ? !!o.waterRoutes : !!o.waterLinks;
    this.dirty = true;
  },

  // what water thing is under the mouse: {type: 'stream', route} | {type: 'trace', link} | {type: 'sinkpoint', sink} | null
  // routes = false: the sinking points and connections; true: the streams in caves
  nearestWater(x, y, rad = 8, routes = false) {
    const o = this.opts; let best = null, bd = rad * rad;
    const segD = (p, q) => {
      if (!p || !q) return Infinity;
      const dx = q[0] - p[0], dy = q[1] - p[1], L = dx * dx + dy * dy || 1, t = Math.max(0, Math.min(1, ((x - p[0]) * dx + (y - p[1]) * dy) / L));
      return (p[0] + t * dx - x) ** 2 + (p[1] + t * dy - y) ** 2;
    };
    this.world.updateMatrixWorld();
    if (!routes && o.waterChannels && this.sinkPtObj) {
      const a = this.sinkPtObj.geometry.attributes.position, v = new THREE.Vector3(), S = this.channelsDoc.sinks;
      for (let i = 0; i < S.length; i++) { const s = this.screen(v.fromBufferAttribute(a, i), this.world); if (!s) continue;
        const dd = (s[0] - x) ** 2 + (s[1] - y) ** 2; if (dd < Math.max(bd, 100)) { bd = dd; best = { type: 'sinkpoint', id: i, sink: S[i] }; } }
      if (best) return best;
    }
    if (routes && o.waterRoutes) for (const h of this.routeHits || []) for (const run of h.runs) {
      let prev = this.screen(run[0], this.cave);
      for (let k = 1; k < run.length; k++) { const cur = this.screen(run[k], this.cave); const dd = segD(prev, cur); if (dd < bd) { bd = dd; best = { type: 'stream', id: h.r.id, route: h.r }; } prev = cur; }
    }
    if (!routes && o.waterLinks) for (const h of this.linkHits || []) {
      if (h.ev === 'negative' && !o.waterNeg) continue;
      const dd = segD(this.screen(h.a, this.world), this.screen(h.b, this.world));
      if (dd < bd) { bd = dd; best = { type: 'trace', id: h.l.id, link: h.l }; }
    }
    return best;
  },

  waterTip(w) {
    const esc = (t) => String(t ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    if (w.type === 'sinkpoint') return `<b>Stream sinks</b><div class="muted">LIDAR · drains ${(w.sink.drains / 1e6).toFixed(2)} km²</div>`;
    if (w.type === 'stream') return `<b>${esc(w.route.name)}</b><div class="muted">Cave stream · ${esc(w.route.entry)} → ${esc(w.route.exit)}</div>`;
    const l = w.link, st = LINK_STYLE[l.evidence] || LINK_STYLE.inferred;
    return `<b>${esc(l.a?.label)} → ${esc(l.b?.label)}</b><div class="muted">${esc(st.label)}${l.when ? ' · ' + esc(l.when) : ''}</div>`;
  },

  // fly to show a stream or a connection
  flyToWater(sel) {
    const b = new THREE.Box3();
    if (sel.type === 'stream') {
      const h = (this.routeHits || []).find(x => x.r.id === sel.id); if (!h) return;
      for (const run of h.runs) for (const v of run) b.expandByPoint(v.clone().setY(v.y + this.opts.shift));
    } else {
      const h = (this.linkHits || []).find(x => x.l.id === sel.id); if (!h) return;
      b.expandByPoint(h.a); b.expandByPoint(h.b);
    }
    if (b.isEmpty()) return;
    const c = b.getCenter(new THREE.Vector3()), s = b.getSize(new THREE.Vector3());
    this.flyToLocal(c, Math.max(80, Math.hypot(s.x, s.z) * 0.75), sel.type === 'stream');
  },
};
